/**
 * Results Middleware — Custom Redux middleware for async avail data fetching.
 *
 * We use a custom middleware instead of redux-thunk because it keeps action creators
 * pure and synchronous, centralizes all API call logic in one place, and gives us
 * access to post-reduction state (via getState after next(action)) which thunks
 * cannot provide without coupling to store internals.
 *
 * @module results-middleware
 */
import axios from 'axios';
import {parserFunction} from '../../helpers/parsers';
import {actionTypes} from '../action-types';
import {AVAIL_ENDPOINT, AVAIL_ERRORS_ENDPOINT} from '../../helpers/constants';

const getResultsCollectionToSave = (endpoint, response) =>
  endpoint === AVAIL_ENDPOINT
    ? response.avails || []
    : endpoint === AVAIL_ERRORS_ENDPOINT
    ? response.availErrors || []
    : [];

/**
 * Handles a fresh search — resets pagination to page 0 and fetches initial results.
 * Called when the user submits a new search from the form, discarding previous results.
 *
 * @param {Function} dispatch - Redux dispatch to emit loading/done/error actions
 * @param {Object} payload - Contains endpoint, searchType, and user-supplied params
 */
const handleFindNewResults = async (dispatch, payload) => {
  const {endpoint, searchType, params: incomengParams} = payload;

  dispatch({
    type: actionTypes.RESULTS_LOADING,
    payload: true,
  });

  const newParams = {
    ...incomengParams,
    limit: 10,
    page: 0,
  };

  const parsedURL = parserFunction({
    endpoint,
    searchType,
    params: newParams,
  });

  try {
    const {data} = await axios.get(parsedURL);
    const resultsToSave = getResultsCollectionToSave(endpoint, data);

    dispatch({
      type: actionTypes.RESULTS_LOADING_FRESH_RESULTS_DONE,
      payload: {
        results: resultsToSave,
        total: data.total,
        endpoint,
        params: newParams,
        searchType,
      },
      loading: false,
    });
  } catch (error) {
    const {statusText} = error?.response;

    dispatch({
      type: actionTypes.RESULTS_EMIT_ERROR,
      payload: {
        errorMessage: error.message,
        errorStatus: statusText,
        loading: false,
      },
    });
  }
};

/**
 * Handles page navigation — re-fetches results for a specific page number
 * while preserving the current search parameters and endpoint.
 *
 * @param {Function} dispatch - Redux dispatch
 * @param {number} page - Target page number
 * @param {Object} state - Current Redux state containing results metadata
 */
const handleUpdateResultsBasedOnPage = async (dispatch, page, {results}) => {
  dispatch({
    type: actionTypes.RESULTS_LOADING,
    payload: true,
  });

  const {endpoint, params, searchType} = results;

  const newParams = {
    ...params,
    page,
  };

  const parsedURL = parserFunction({
    endpoint,
    params: newParams,
    searchType,
  });

  try {
    const {data} = await axios.get(parsedURL);

    const resultsToSave = getResultsCollectionToSave(endpoint, data);

    dispatch({
      type: actionTypes.RESULTS_UPDATING_PAGE_DONE,
      payload: {
        results: resultsToSave,
        page,
        params: newParams,
        loading: false,
      },
    });
  } catch (error) {
    const {statusText} = error?.response;

    dispatch({
      type: actionTypes.RESULTS_EMIT_ERROR,
      payload: {
        errorMessage: error.message,
        errorStatus: statusText,
        loading: false,
      },
    });
  }
};

/**
 * Handles page-limit changes — resets to page 0 and re-fetches with the new limit.
 * Resets page because changing the number of results per page invalidates the current offset.
 *
 * @param {Function} dispatch - Redux dispatch
 * @param {number} limit - New results-per-page value
 * @param {Object} state - Current Redux state containing results metadata
 */
export const handleChangePageLimitSize = async (dispatch, limit, {results}) => {
  dispatch({
    type: actionTypes.RESULTS_LOADING,
    payload: true,
  });

  const {endpoint, params, searchType} = results;

  const newParams = {
    ...params,
    limit,
    page: 0,
  };

  const parsedURL = parserFunction({
    endpoint,
    params: newParams,
    searchType,
  });

  try {
    const {data} = await axios.get(parsedURL);

    const resultsToSave = getResultsCollectionToSave(endpoint, data);

    dispatch({
      type: actionTypes.RESULTS_UPDATING_PAGE_LIMIT_DONE,
      payload: {
        results: resultsToSave,
        params: newParams,
        loading: false,
      },
    });
  } catch (error) {
    const {statusText} = error?.response;

    dispatch({
      type: actionTypes.RESULTS_EMIT_ERROR,
      payload: {
        errorMessage: error.message,
        errorStatus: statusText,
        loading: false,
      },
    });
  }
};

/**
 * Redux middleware that intercepts results-related actions and performs async API fetches.
 *
 * Flow: component dispatches intent action → middleware passes it through (next) →
 * middleware detects the action type → triggers async fetch → dispatches outcome action
 * (done/error) which the reducer handles to update state.
 */
export const resultsMiddleware = store => next => action => {
  const {dispatch, getState} = store;
  next(action);
  const nextState = getState();

  // Action routing map — maps intent actions to their async handler functions.
  // This pattern replaces a switch statement and keeps handler logic decoupled.
  const actions = {
    [actionTypes.RESULTS_LOADING_FRESH_RESULTS]: () => handleFindNewResults(dispatch, action.payload),
    [actionTypes.RESULTS_UPDATING_PAGE]: () => handleUpdateResultsBasedOnPage(dispatch, action.payload, nextState),
    [actionTypes.RESULTS_UPDATING_PAGE_LIMIT]: () => handleChangePageLimitSize(dispatch, action.payload, nextState),
  };

  if (actions[action.type]) {
    return actions[action.type]();
  }
};
